import { useMemo, useRef, useLayoutEffect } from "react";
import { SearchCountResponse } from "../../../../common/api/decimal/methods/search";
import ResultSection from "../common/ResultSection";
import { FormattedMessage } from "react-intl";
import GridResultCard from "./GridResultCard";
import styles from "./FavoritesResults.module.scss";
import { ManageAssetWithLicense } from "../../../../common/api/decimal/common-types/manageAssets";
import useSearchOutletContext from "../layout/outletContext";
import { IsFavorite } from "../../../../common/api/decimal/common-types/manageAssets";
import { getFocusableElements } from "../../../../common/utils/dom";
import { makeSearchReturnDetail, SearchSection } from "./types.ts";
import MoreButton from "../../../../common/core-ui/MoreButton.tsx";
import useMore from "../../../../common/core-ui/useMore.tsx";

const FAVORITES_CHUNK_SIZE = 4;

export default function FavoritesResults({
  openPanel,
}: {
  openPanel: (asset: ManageAssetWithLicense, section: SearchSection) => void;
}) {
  const {
    contentResults: { allResults, favoritedContentVbids },
  } = useSearchOutletContext();
  const focusRef = useRef<HTMLLIElement>(null);

  const favoritedResults = useMemo(
    () =>
      allResults
        ?.filter((hit) => favoritedContentVbids?.indexOf(hit.vbid) !== -1)
        .sort((h1, h2) => {
          const getFaveTime = (date: string) => new Date(date).getTime();
          return (
            getFaveTime((h2.asset.favorite as IsFavorite).favoritedAt) -
            getFaveTime((h1.asset.favorite as IsFavorite).favoritedAt)
          );
        }) ?? [],
    [allResults, favoritedContentVbids]
  );
  const {
    itemsToShow: favorites,
    hasMore,
    loadMore,
    focusElementIndex,
  } = useMore<SearchCountResponse>(favoritedResults, FAVORITES_CHUNK_SIZE);

  useLayoutEffect(() => {
    if (focusElementIndex && focusRef.current) {
      getFocusableElements(focusRef.current)[0]?.focus();
    }
  }, [focusElementIndex, focusRef]);

  if (!!favorites.length && favorites.length > 0) {
    return (
      <ResultSection>
        <FormattedMessage
          defaultMessage="Content Results in Favorites"
          tagName="h2"
        />
        <ul className={styles.list}>
          {favorites.map((result, index) => {
            return (
              <GridResultCard
                searchResult={result}
                openPanel={openPanel}
                key={result.asset.manageAsset.vbid}
                dynamicStyles={{ zIndex: -index }}
                ref={index === focusElementIndex ? focusRef : undefined}
                section={"favorite"}
                libraryReturn={makeSearchReturnDetail(
                  "favorite",
                  result.asset.manageAsset.vbid
                )}
              />
            );
          })}
        </ul>
        <MoreButton hasMore={hasMore} onMoreClick={loadMore} />
      </ResultSection>
    );
  }
}
